'use client';

import { useEffect } from 'react';
import { Typography, Box, Container } from '@mui/material';
import { PartCardHorizontal } from '@qb/frontend/components/PartCards/PartCardHorizontal';
import { PartCardLarge } from '@qb/frontend/components/PartCards/PartCardLarge';
import { useIsUnderBreakpoint } from '@qb/frontend/hooks/mediaQueries';
import { BaseCarousel } from '@/components/Common/BaseCarousel/BaseCarousel';
import { useRequestPartPriceFromSPW } from '@/hooks/useRequestPartPriceFromSPW';
import { TenantConfig } from '@/shared/types/controllers/sps/SPSAppControllerTypes';
import { PartGetSamePartCategoryPartsResponse, PartDisplayDataResponse } from '@/shared/types/controllers/sps/SPSPartControllerTypes';

// TODO: Consider fetching this from an API or configuration
const SHIP_PROCESSING_DAYS = 0;
type Props = {
  tenantConfig: TenantConfig;
  partCategory: NonNullable<PartDisplayDataResponse['partCategoriesToRoot'][0]>;
  authUserCompanyName?: string;
  parts: PartGetSamePartCategoryPartsResponse['parts'];
};
const SimilarProductsFromSameCategoryCarouselDesktop = ({
  tenantConfig,
  parts,
  authUserCompanyName
}: Props) => {
  return <Box sx={{
    '& .slick-track': {
      display: 'flex !important'
    },
    '& .slick-slide': {
      height: 'inherit !important'
    },
    '& .slick-slide > div': {
      height: '100%'
    }
  }} data-sentry-element="Box" data-sentry-component="SimilarProductsFromSameCategoryCarouselDesktop" data-sentry-source-file="SimilarProductsFromSameCategoryCarousel.tsx">
      <BaseCarousel slidesCount={parts.length} slidesToShow={{
      xs: 1,
      sm: 2,
      md: 3,
      lg: 4,
      xl: 4
    }} data-sentry-element="BaseCarousel" data-sentry-source-file="SimilarProductsFromSameCategoryCarousel.tsx">
        {parts.map(part => <Box key={part.id} sx={{
        px: 1,
        height: '100%'
      }}>
            <PartCardLarge tenantConfig={tenantConfig} authUserCompanyName={authUserCompanyName} part={part} />
          </Box>)}
      </BaseCarousel>
    </Box>;
};
const SimilarProductsFromSameCategoryCarouselMobile = ({
  tenantConfig,
  parts
}: Props) => {
  return <Box sx={{
    display: 'grid'
  }} data-sentry-element="Box" data-sentry-component="SimilarProductsFromSameCategoryCarouselMobile" data-sentry-source-file="SimilarProductsFromSameCategoryCarousel.tsx">
      {parts.map(part => <PartCardHorizontal key={part.id} part={part} tenantConfig={tenantConfig} />)}
    </Box>;
};
export const SimilarProductsFromSameCategoryCarousel = ({
  tenantConfig,
  parts,
  partCategory
}: Props) => {
  const isMobile = useIsUnderBreakpoint('sm');
  const {
    authUser,
    requestMultiplePartsPrices,
    getPartPrice
  } = useRequestPartPriceFromSPW();
  useEffect(() => {
    const partIDs = parts.map(({
      id
    }) => id);

    /**
     * Request the prices for the parts carousel.
     */
    requestMultiplePartsPrices(partIDs);
  }, [getPartPrice, parts, requestMultiplePartsPrices]);
  const partsWithDiscounts = parts.map(part => {
    const partPrice = getPartPrice(part.id);
    return {
      ...part,
      price: partPrice?.price ?? undefined,
      originalPrice: partPrice?.originalPrice ?? undefined,
      shipProcessingDays: SHIP_PROCESSING_DAYS
    };
  });
  if (!parts?.length) {
    return null;
  }
  return <Box sx={{
    bgcolor: [null, 'common.backgroundColor'],
    pt: [0, 3, 4],
    pb: [0, 3, 5],
    mt: [3, 3, 5]
  }} data-sentry-element="Box" data-sentry-component="SimilarProductsFromSameCategoryCarousel" data-sentry-source-file="SimilarProductsFromSameCategoryCarousel.tsx">
      <Container data-sentry-element="Container" data-sentry-source-file="SimilarProductsFromSameCategoryCarousel.tsx">
        <Typography variant="h4" component="h2" sx={{
        typography: {
          xs: 'h5',
          sm: 'h4'
        },
        mb: [1.5, 2, 3]
      }} data-sentry-element="Typography" data-sentry-source-file="SimilarProductsFromSameCategoryCarousel.tsx">
          Similar products from: {partCategory.name}
        </Typography>
        {isMobile ? <SimilarProductsFromSameCategoryCarouselMobile tenantConfig={tenantConfig} partCategory={partCategory} authUserCompanyName={authUser?.company?.name} parts={partsWithDiscounts} /> : <SimilarProductsFromSameCategoryCarouselDesktop tenantConfig={tenantConfig} partCategory={partCategory} authUserCompanyName={authUser?.company?.name} parts={partsWithDiscounts} />}
      </Container>
    </Box>;
};